import { Editor } from '@tiptap/core';
import { useCallback, useEffect, useRef, useState } from 'react';

import { LinkPreviewData, useFetchLinkPreview } from '../../../queries/link-previews';
import { AttachmentCategory, LinkPreviewAttachment } from '../lib/entities/attachment';

// URL regex that matches common URL patterns
const URL_REGEX = /https?:\/\/[^\s<>"{}|\\^`[\]]+/gi;

// Strip common trailing punctuation that gets captured from natural text
// e.g., "Check out https://example.com!" → "https://example.com"
const stripTrailingPunctuation = (url: string): string => url.replace(/[.,;:!?)]+$/, '');

// Debounce delay in milliseconds
const DEBOUNCE_DELAY = 500;

const normalizeUrlForCompare = (url: string): string => {
	try {
		const parsed = new URL(url);
		const normalizedPath = parsed.pathname.replace(/\/+$/, '') || '/';
		return `${parsed.protocol}//${parsed.host}${normalizedPath}${parsed.search}${parsed.hash}`;
	} catch {
		return url.replace(/\/+$/, '');
	}
};

type ProseMirrorLinkMark = {
	type?: { name?: string };
	attrs?: { href?: string };
};

type ProseMirrorTextNode = {
	isText?: boolean;
	text?: string;
	nodeSize?: number;
	marks?: ProseMirrorLinkMark[];
};

type ProseMirrorTransaction = {
	insertText: (text: string, from: number, to: number) => ProseMirrorTransaction;
	addMark: (from: number, to: number, mark: unknown) => ProseMirrorTransaction;
};

type ProseMirrorDoc = {
	descendants: (cb: (node: ProseMirrorTextNode, pos: number) => boolean | void) => void;
};

type ProseMirrorLinkMarkType = {
	create: (attrs: { href: string }) => unknown;
};

type LinkRange = {
	from: number;
	to: number;
	href: string;
};

const isProseMirrorDoc = (value: unknown): value is ProseMirrorDoc => {
	return typeof value === 'object' && value !== null && typeof (value as { descendants?: unknown }).descendants === 'function';
};

const isProseMirrorTransaction = (value: unknown): value is ProseMirrorTransaction => {
	if (typeof value !== 'object' || value === null) {
		return false;
	}

	const transaction = value as { insertText?: unknown; addMark?: unknown };
	return typeof transaction.insertText === 'function' && typeof transaction.addMark === 'function';
};

const isProseMirrorLinkMarkType = (value: unknown): value is ProseMirrorLinkMarkType => {
	return typeof value === 'object' && value !== null && typeof (value as { create?: unknown }).create === 'function';
};

const findMatchingRawUrlLinkRange = (doc: ProseMirrorDoc, url: string): LinkRange | null => {
	const normalizedFetchedUrl = normalizeUrlForCompare(url);
	let foundRange: LinkRange | null = null;

	doc.descendants((node, pos) => {
		if (!node?.isText || !node.text) {
			return;
		}

		const linkMark = node.marks?.find((mark) => mark.type?.name === 'link' && mark.attrs?.href);
		if (!linkMark) {
			return;
		}

		const href = linkMark.attrs?.href;
		const nodeSize = node.nodeSize;
		if (!href || !nodeSize) {
			return;
		}

		const normalizedHref = normalizeUrlForCompare(href);
		const normalizedNodeText = normalizeUrlForCompare(stripTrailingPunctuation(node.text));
		if (normalizedHref !== normalizedFetchedUrl || normalizedNodeText !== normalizedHref) {
			return;
		}

		foundRange = {
			from: pos,
			to: pos + nodeSize,
			href,
		};

		return false;
	});

	return foundRange;
};

const replaceRawUrlLinkTextWithTitle = (editor: Editor, url: string, title: string) => {
	if (!title) {
		return;
	}

	const doc = editor.state?.doc;
	const tr = editor.state?.tr;
	const linkMarkType = editor.state?.schema?.marks?.link;
	const dispatch = editor.view?.dispatch;

	if (!isProseMirrorDoc(doc) || !isProseMirrorTransaction(tr) || !isProseMirrorLinkMarkType(linkMarkType) || !dispatch) {
		return;
	}

	const linkRange = findMatchingRawUrlLinkRange(doc, url);
	if (!linkRange) {
		return;
	}

	const nextTr = tr.insertText(title, linkRange.from, linkRange.to);
	nextTr.addMark(linkRange.from, linkRange.from + title.length, linkMarkType.create({ href: linkRange.href }));
	dispatch(nextTr);
};

interface UseLinkPreviewOptions {
	editor: Editor | null;
	attachment: unknown | null;
	setAttachment: (attachment: LinkPreviewAttachment | null) => void;
	enabled?: boolean;
}

/**
 * Hook to detect URLs in editor text and fetch Open Graph previews.
 * Only fetches a preview if no attachment is already set.
 * Link previews have lower priority than other attachment types (video, GIF, image).
 */
export const useLinkPreview = ({ editor, attachment, setAttachment, enabled = true }: UseLinkPreviewOptions) => {
	const [isLoading, setIsLoading] = useState(false);
	const lastFetchedUrlRef = useRef<string | null>(null);
	const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
	// Track current attachment to avoid overwriting higher-priority attachments (video, GIF, etc.)
	const attachmentRef = useRef(attachment);
	attachmentRef.current = attachment;

	const { mutate: fetchLinkPreview } = useFetchLinkPreview({
		onSuccess: (data: LinkPreviewData) => {
			if (editor && data.title) {
				replaceRawUrlLinkTextWithTitle(editor, data.url, data.title);
			}

			// Don't override existing attachments - they have higher priority than link previews
			if (attachmentRef.current) {
				setIsLoading(false);
				return;
			}

			// Only set attachment if we got an image - skip links that only return minimal metadata
			// (e.g., auth-gated pages like Slack that only return site_name)
			if (data.image_url) {
				const linkPreviewAttachment = new LinkPreviewAttachment({
					category: AttachmentCategory.LinkPreview,
					url: data.url,
					title: data.title,
					description: data.description,
					image_url: data.image_url,
					site_name: data.site_name,
				});
				setAttachment(linkPreviewAttachment);
			}
			setIsLoading(false);
		},
		onError: () => {
			setIsLoading(false);
		},
	});

	const detectAndFetchUrl = useCallback(() => {
		if (!editor || !enabled) return;

		// Don't fetch if an attachment is already set
		if (attachment) return;

		const text = editor.getText();
		const matches = text.match(URL_REGEX);

		if (!matches || matches.length === 0) {
			lastFetchedUrlRef.current = null;
			return;
		}

		// Use the first URL found, stripping any trailing punctuation
		const url = stripTrailingPunctuation(matches[0]);

		// Don't re-fetch the same URL
		if (url === lastFetchedUrlRef.current) return;

		lastFetchedUrlRef.current = url;
		setIsLoading(true);
		fetchLinkPreview(url);
	}, [editor, enabled, attachment, fetchLinkPreview]);

	// Listen for editor updates with debouncing
	useEffect(() => {
		if (!editor || !enabled) return;

		const handleUpdate = () => {
			// Clear existing timer
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}

			// Set new timer
			debounceTimerRef.current = setTimeout(() => {
				detectAndFetchUrl();
			}, DEBOUNCE_DELAY);
		};

		editor.on('update', handleUpdate);

		return () => {
			editor.off('update', handleUpdate);
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
		};
	}, [editor, enabled, detectAndFetchUrl]);

	// Reset lastFetchedUrl when attachment is removed
	useEffect(() => {
		if (!attachment) {
			lastFetchedUrlRef.current = null;
		}
	}, [attachment]);

	return {
		isLoading,
	};
};
